import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../services/authService";

type PublicMatchDetails = {
  matchmaking?: {
    id?: string;
    mode?: string;
    status?: string;
    level?: string | null;
    note?: string | null;
    description?: string | null;
    ownerName?: string;
    currentPlayers?: number;
    targetPlayers?: number;
    full?: boolean;
  };
  appointment?: {
    start_at?: string;
    end_at?: string;
    service?: { name?: string };
    unit?: {
      name?: string;
      short_name?: string;
      business?: { name?: string; address?: string };
    };
  };
  app?: {
    brand?: string | null;
    scheme?: string | null;
    downloadUrl?: string | null;
    schemeUrl?: string | null;
  };
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PublicMatch: React.FC = () => {
  const { matchmakingId = "" } = useParams();
  const [details, setDetails] = useState<PublicMatchDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE_URL,
        timeout: 20000,
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadMatch = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get<PublicMatchDetails>(
          `/appointments/matchmaking/${encodeURIComponent(matchmakingId)}/public`
        );
        if (!cancelled) setDetails(response.data);
      } catch (err: any) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (matchmakingId) {
      loadMatch();
    } else {
      setError("Match link is invalid.");
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [api, matchmakingId]);

  const match = details?.matchmaking || {};
  const appointment = details?.appointment || {};
  const unit = appointment.unit || {};
  const venue = unit.business || {};
  const status = String(match.status || "UNKNOWN").toUpperCase();
  const mode = String(match.mode || "PUBLIC").toUpperCase();
  const currentPlayers = Number(match.currentPlayers || 1);
  // Follows the org's share divisor now — a basketball org sends 10. Render
  // whatever the API returns instead of assuming a padel four.
  const targetPlayers = Number(match.targetPlayers) || 0;
  // Tenant-resolved by the API from app_versions. Never hardcode a scheme here:
  // each white-label app registers its own ("padel365", "allplay", ...) and the
  // wrong one silently opens whichever other app claims it.
  const appSchemeUrl = details?.app?.schemeUrl || "";
  const downloadUrl = details?.app?.downloadUrl || "";
  const brand = details?.app?.brand || "";

  return (
    <main style={styles.page}>
      <section style={styles.card}>
        <div style={styles.brand}>{brand || "Booking"}</div>
        {loading ? (
          <p style={styles.muted}>Loading match...</p>
        ) : error && !details ? (
          <>
            <h1 style={styles.title}>Match unavailable</h1>
            <p style={styles.muted}>{error}</p>
          </>
        ) : (
          <>
            <span style={styles.status}>{mode} MATCH · {status}</span>
            <h1 style={styles.title}>Join this match{brand ? ` on ${brand}` : ""}</h1>

            <InfoRow label="Venue" value={venue.name || "-"} />
            <InfoRow label="Court" value={unit.short_name || unit.name || "-"} />
            <InfoRow label="Service" value={appointment.service?.name || "-"} />
            <InfoRow label="Start" value={formatDateTime(appointment.start_at)} />
            <InfoRow
              label="Players"
              value={
                targetPlayers > 0
                  ? `${currentPlayers}/${targetPlayers}`
                  : String(currentPlayers)
              }
            />
            {match.level && <InfoRow label="Level" value={String(match.level)} />}
            {(match.note || match.description) && (
              <InfoRow label="Note" value={String(match.note || match.description)} />
            )}
            {venue.address && <InfoRow label="Address" value={venue.address} />}

            <div style={styles.actions}>
              {appSchemeUrl && (
                <a href={appSchemeUrl} style={styles.primary}>
                  Open in app
                </a>
              )}
              {downloadUrl && (
                <a href={downloadUrl} style={styles.secondary}>
                  Download app
                </a>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div style={styles.row}>
    <div style={styles.label}>{label}</div>
    <div style={styles.value}>{value}</div>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#f6f6f6",
    color: "#212121",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 460,
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 8,
    padding: 20,
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    boxSizing: "border-box",
  },
  brand: {
    background: "#212121",
    color: "#fff",
    borderRadius: 8,
    padding: 16,
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 18,
  },
  status: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#f0f0f0",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 14,
  },
  title: {
    fontSize: 22,
    margin: "0 0 14px",
  },
  row: {
    marginTop: 12,
  },
  label: {
    color: "#666",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  value: {
    marginTop: 4,
    fontSize: 16,
    fontWeight: 600,
  },
  muted: {
    color: "#666",
    lineHeight: 1.45,
  },
  actions: {
    marginTop: 22,
    display: "grid",
    gap: 10,
  },
  primary: {
    minHeight: 48,
    borderRadius: 8,
    background: "#212121",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    fontWeight: 700,
  },
  secondary: {
    minHeight: 48,
    borderRadius: 8,
    background: "#fff",
    color: "#212121",
    border: "1px solid #212121",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    fontWeight: 700,
  },
};

export default PublicMatch;
